import React, { useState, useEffect } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import Button from '../ui/Button';
import Input from '../ui/Input';
import Select from '../ui/Select';
import { supabase } from '../../lib/supabase';

interface UserWithRole {
  id: string;
  email: string;
  role: string;
  created_at: string;
}

interface UserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (user: UserWithRole) => void;
  initialData?: UserWithRole | null;
}

const UserModal: React.FC<UserModalProps> = ({
  isOpen,
  onClose,
  onSave,
  initialData,
}) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState('Vendedor');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Set initial values when editing
  useEffect(() => {
    if (initialData) {
      setEmail(initialData.email);
      setRole(initialData.role);
      setPassword(''); // Clear password when editing
    } else {
      setEmail('');
      setRole('Vendedor');
      setPassword('');
    }
    setError(null);
  }, [initialData, isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      if (initialData) {
        // Update existing user's role
        const { error } = await supabase.functions.invoke('update-user-role', {
          body: { userId: initialData.id, role }
        });

        if (error) throw new Error(error.message);

        // Return updated user
        onSave({
          ...initialData,
          role,
        });
      } else {
        // Create new user
        if (!password) {
          setError('La contraseña es obligatoria');
          setIsLoading(false);
          return;
        }

        // Validate password
        if (password.length < 8) {
          setError('La contraseña debe tener al menos 8 caracteres');
          setIsLoading(false);
          return;
        }

        // Create user with role directly
        const { data, error } = await supabase.functions.invoke('create-user', {
          body: { email, password, role }
        });

        if (error) {
          throw new Error(error.message);
        }

        if (data?.user) {
          onSave({
            id: data.user.id,
            email,
            role,
            created_at: new Date().toISOString(),
          });
        } else {
          throw new Error('No se pudo crear el usuario');
        }
      }
    } catch (error: any) {
      console.error('Error saving user:', error);
      setError(error.message || 'Error al guardar el usuario');
    } finally {
      setIsLoading(false);
    }
  };

  // Generate a random password
  const generatePassword = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*()';
    let result = '';
    for (let i = 0; i < 12; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setPassword(result);
  };

  return (
    <Dialog.Root open={isOpen} onOpenChange={onClose}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/50" />
        <Dialog.Content className="fixed left-[50%] top-[50%] max-h-[85vh] w-[90vw] max-w-[500px] translate-x-[-50%] translate-y-[-50%] rounded-lg bg-white dark:bg-gray-800 p-6 shadow-lg overflow-y-auto">
          <Dialog.Title className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">
            {initialData ? 'Editar Usuario' : 'Nuevo Usuario'}
          </Dialog.Title>
          
          <form onSubmit={handleSubmit}>
            <div className="space-y-4">
              <Input
                label="Email"
                id="email"
                name="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={!!initialData} // Disable email field when editing
              />
              
              {!initialData && (
                <div>
                  <div className="flex gap-2">
                    <Input
                      label="Contraseña"
                      id="password"
                      name="password"
                      type="text" // Use text to show the generated password
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      className="flex-1"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      className="mt-7"
                      onClick={generatePassword}
                    >
                      Generar
                    </Button>
                  </div>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    La contraseña debe tener al menos 8 caracteres.
                  </p>
                </div>
              )}
              
              <Select
                label="Rol"
                value={role}
                onChange={setRole}
                options={[
                  { value: 'Administrador', label: 'Administrador' },
                  { value: 'Vendedor', label: 'Vendedor' },
                  { value: 'Producción', label: 'Producción' },
                  { value: 'Depósito', label: 'Depósito' }
                ]}
                required
              />
            </div>
            
            {error && (
              <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded-md text-sm">
                {error}
              </div>
            )}
            
            <div className="mt-6 flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={isLoading || (!initialData && !password)}
              >
                {isLoading ? 'Guardando...' : initialData ? 'Guardar Cambios' : 'Crear Usuario'}
              </Button>
            </div>
          </form>
          
          <Dialog.Close asChild>
            <button
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
              aria-label="Close"
            >
              <X className="h-6 w-6" />
            </button>
          </Dialog.Close>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default UserModal;